<template>
  <article v-if="ready" ref="root" class="sp-finder-result" :class="classModifiers" part="base">
    <FinderResultThumbnail class="sp-finder-result__thumbnail" v-bind="thumbnailProps" />

    <div class="sp-finder-result__info">
      <FinderResultHeading
        class="sp-finder-result__heading"
        :name="displayName"
        :url="result.url"
        :provider-type="result.provider_type"
        :skills="result.skills"
        :show-specialty="showSpecialty"
        @add-filters="handleAddFilters"
      />

      <div class="sp-finder-result__body">
        <div v-if="hasAddressRegion" class="sp-finder-result__address-region">
          <FinderResultAddress
            :result="result"
            :result-type="resultType"
            :result-config="resultConfig"
            :labels="labels"
          />

          <FinderResultHoursBadge v-if="showHoursBadge" :availability="hoursBadgeAvailability" />
        </div>

        <FinderResultLocationActions
          v-if="hasAddress"
          :result="result"
          :result-config="resultConfig"
          @show-map="handleShowMap"
        />

        <FinderResultAttributes
          :result="result"
          :result-type="resultType"
          :result-config="resultConfig"
          :is-directory-layout="isDirectoryLayout"
          :labels="labels"
        />
      </div>
    </div>

    <FinderResultDirectoryDetails
      v-if="isDirectoryLayout"
      class="sp-finder-result__details"
      :layout="resultLayout"
      v-bind="directoryDetailsProps"
    />
  </article>
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useAddressAvailabilityFetch } from "#ce/composables/useAddressAvailabilityFetch";
import { useContainerBreakpoints } from "#ce/composables/useContainerBreakpoints";
import { whenI18nReady } from "./i18n";
import FinderResultAddress from "./components/FinderResultAddress.ce.vue";
import FinderResultAttributes from "./components/FinderResultAttributes.ce.vue";
import FinderResultDirectoryDetails from "./components/FinderResultDirectoryDetails.ce.vue";
import FinderResultHeading from "./components/FinderResultHeading.ce.vue";
import FinderResultHoursBadge from "./components/FinderResultHoursBadge.ce.vue";
import FinderResultLocationActions from "./components/FinderResultLocationActions.ce.vue";
import FinderResultThumbnail from "./components/FinderResultThumbnail.ce.vue";
import { props as propsDefinitions } from "./props/finder-result.js";

const emit = defineEmits(["add-filters", "show-map"]);

// Gate first paint until the finder strings are hydrated so child labels never
// flash their i18n keys before the catalog resolves.
const ready = ref(false);
onMounted(async () => {
  await whenI18nReady;
  ready.value = true;
});

const props = defineProps(propsDefinitions);

const root = useTemplateRef("root");
const { isMedium, isLarge, isXLarge } = useContainerBreakpoints(root, {
  small: { max: 559 },
  medium: { min: 560, max: 879 },
  large: { min: 880, max: 1079 },
  xlarge: { min: 1080 },
});

// Result identity
const resultType = computed(() => {
  if (props.result.resultType === "Location") {
    return "location";
  }
  if (props.result.resultType === "Physician") {
    return "provider";
  }

  return Array.isArray(props.result.physician_image_urls) ? "location" : "provider";
});
const displayName = computed(() => props.result.label || props.result.name || "");

// Config flags
const isDirectoryLayout = computed(() => props.resultConfig?.resultTemplate === "directory");
const showSpecialty = computed(() => Boolean(props.resultConfig?.showProviderSpecialty));
// Gates the location actions (Show on Map / distance / more-locations) — they render only
// when address display is on, preserving the legacy behaviour where they lived inside the
// `<address v-if="hasAddress">` block.
const hasAddress = computed(() => Boolean(props.resultConfig?.showProviderAddress));
const fetchHoursBadge = computed(() =>
  Boolean(props.resultConfig?.showProviderHoursBadge && props.result.hours_badge_url),
);
const { availability: hoursBadgeAvailability } = useAddressAvailabilityFetch({
  url: computed(() => props.result.hours_badge_url),
  enabled: fetchHoursBadge,
});
const showHoursBadge = computed(() => Boolean(hoursBadgeAvailability.value));
// Wraps the address and its hours badge into one region so they sit closer together
// (via --address-region-gap) than the other body regions. Rendered when either is shown.
const hasAddressRegion = computed(() => hasAddress.value || showHoursBadge.value);

// Layout
const resultLayout = computed(() => {
  if (isXLarge.value) {
    return "xl";
  }

  if (isLarge.value) {
    return "lg";
  }

  if (isMedium.value) {
    return "md";
  }

  return "sm";
});

// Child props
const thumbnailProps = computed(() => {
  const result = props.result;
  const isProvider = resultType.value === "provider";
  const isLocation = resultType.value === "location";

  return {
    resultType: resultType.value,
    name: displayName.value,
    url: result.url,
    imageUrl: result.image_url,
    physicianImageUrls: result.physician_image_urls || [],
    physicianCountLabel: isLocation && result.physician_count ? result.physician_count_label : "",
    badge: isProvider ? result.badge : null,
    independentBadgeText: isProvider ? result.independent_badge_text : "",
    awards: isProvider ? result.awards || [] : [],
  };
});

const directoryDetailsProps = computed(() => {
  const result = props.result;
  const config = props.resultConfig;

  return {
    // Normalized "provider" | "location" — selects the details panel's section set.
    resultType: resultType.value,
    phoneNumber: config?.showProviderPhoneNumber ? result.phone_number : "",
    faxNumber: config?.showProviderFaxNumber ? result.fax_number : "",
    gender: result.gender,
    languages: result.languages || [],
    certifications: result.certifications || [],
    educations: result.educations || [],
    plansAcceptedCount: result.plans_accepted_count,
    insurancePlansUrl: result.insurance_plans_url,
    // The disclaimer label is resolved per result type upstream (blank unless a
    // publishing domain configures one); the details panel renders it for locations only.
    disclaimer: props.labels.disclaimer,
    disclaimerHeading: props.labels.disclaimerHeading,
    // Location web contact and services are not serialized yet, so the panel's
    // website/email/services props are left at their defaults (sections stay hidden).
    // Wire them here once a search serializer provides the fields.
  };
});

const classModifiers = computed(() => `--layout-${resultLayout.value}`);

function handleShowMap(payload) {
  emit("show-map", payload);
}

function handleAddFilters(payload) {
  emit("add-filters", payload);
}
</script>

<style>
:host {
  /* Layout */
  --column-gap: var(--sp-comp-finder-result-column-gap, var(--sp-ref-spacing-12));
  --row-gap: var(--sp-comp-finder-result-row-gap, var(--sp-ref-spacing-16));
  --info-gap: var(--sp-comp-finder-result-info-gap, var(--row-gap));
  --body-gap: var(--sp-comp-finder-result-body-gap, var(--sp-ref-spacing-12));
  --address-region-gap: var(--sp-comp-finder-result-address-region-gap, var(--sp-ref-spacing-6));
  --content-min-width: var(--sp-comp-finder-result-content-min-width, 16rem);

  /* Heading */
  --heading-text-align-sm: var(--sp-comp-finder-result-heading-text-align-sm, center);

  /* Thumbnail */
  --thumbnail-width: var(--sp-comp-finder-result-thumbnail-width, 12rem);
  --thumbnail-min-width: var(--sp-comp-finder-result-thumbnail-min-width, 8rem);
  --thumbnail-justify-self-sm: var(--sp-comp-finder-result-thumbnail-justify-self-sm, center);
  --thumbnail-padding-block-sm: var(--sp-comp-finder-result-thumbnail-padding-block-sm, var(--sp-ref-spacing-4));

  /* Details */
  --details-width-lg: var(--sp-comp-finder-result-details-width-lg, 24rem);
  --details-width-xl: var(--sp-comp-finder-result-details-width-xl, 38rem);

  /* Color */
  --background: var(--sp-comp-finder-result-background, transparent);
  --color: var(--sp-comp-finder-result-color, var(--sp-sys-color-on-surface, #333));

  /* Typography */
  --font-family: var(--sp-comp-finder-result-font-family, var(--sp-sys-font-family-normal, inherit));

  display: block;
  width: 100%;
}
</style>

<style lang="scss" scoped>
.sp-finder-result {
  --grid-columns: minmax(0, 1fr);
  --grid-template-areas: "heading" "thumbnail" "body" "details";
  --info-display: contents;

  display: grid;
  width: 100%;
  box-sizing: border-box;
  grid-template-columns: var(--grid-columns);
  grid-template-areas: var(--grid-template-areas);
  gap: var(--row-gap) var(--column-gap);
  align-items: start;
  background: var(--background);
  color: var(--color);
  font-family: var(--font-family);

  &.--layout-sm {
    --heading-text-align: var(--heading-text-align-sm);
    --thumbnail-justify-self: var(--thumbnail-justify-self-sm);
    --thumbnail-padding-block: var(--thumbnail-padding-block-sm);
  }

  &.--layout-md {
    --info-display: flex;
    --grid-columns: minmax(var(--thumbnail-min-width), var(--thumbnail-width)) minmax(0, 1fr);
    --grid-template-areas: "thumbnail info" "details details";
  }

  &.--layout-lg,
  &.--layout-xl {
    --info-display: flex;
    --details-width: var(--details-width-lg);
    --grid-columns: minmax(var(--thumbnail-min-width), var(--thumbnail-width)) minmax(var(--content-min-width), 1fr)
      minmax(0, var(--details-width));
    --grid-template-areas: "thumbnail info details";
  }

  &.--layout-xl {
    --details-width: var(--details-width-xl);
  }

  &__thumbnail {
    grid-area: thumbnail;
    width: var(--thumbnail-width);
    max-width: 100%;
    justify-self: var(--thumbnail-justify-self, auto);
    padding-block: var(--thumbnail-padding-block, 0);
  }

  &__info {
    display: var(--info-display);
    min-width: 0;
    flex-direction: column;
    grid-area: info;
    gap: var(--info-gap);
  }

  &__heading {
    grid-area: heading;
    text-align: var(--heading-text-align, inherit);
  }

  &__body {
    display: flex;
    min-width: 0;
    flex-direction: column;
    grid-area: body;
    gap: var(--body-gap);
  }

  &__address-region {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: var(--address-region-gap);
  }

  &__details {
    grid-area: details;
    min-width: 0;
  }
}
</style>
